/**
 * Der Verlauf des Grundrisseditors — was rückgängig zu machen ist und was wiederherzustellen.
 *
 * Nicht an eine einzelne Handlung gebunden: Ausstechen, eine verschobene Ecke, eine eingefügte,
 * eine neu gezeichnete Raumform — alles legt hier denselben Schritt ab. Ein Schritt weiß, wie er
 * sich zurücknimmt und wie er wiederkommt; der Verlauf weiß nur, in welcher Reihenfolge.
 *
 * Der Zustand ist unveränderlich, damit er in React liegen kann: Jede Bewegung liefert einen neuen
 * Verlauf statt den alten zu ändern.
 */

/** Eine Handlung, die sich zurücknehmen lässt. */
export interface EditStep {
    /**
     * Wofür sie steht — erscheint am Kurztext der Schaltflächen, damit vor dem Klick klar ist,
     * *was* zurückgenommen wird.
     */
    label: string;
    /** Den Zustand von vor der Handlung herstellen. */
    undo: () => void;
    /** Und wieder den danach. */
    redo: () => void;
}

export interface EditHistory {
    /** Geschehene Schritte, der jüngste zuletzt. */
    past: EditStep[];
    /** Zurückgenommene Schritte, der zuletzt zurückgenommene zuletzt. */
    future: EditStep[];
}

export const EMPTY_HISTORY: EditHistory = { past: [], future: [] };

/**
 * Wie weit zurück der Verlauf reicht.
 *
 * Eine Grenze ist nötig, weil jeder Schritt Abschlüsse über Raumformen festhält — ein Editor, der
 * eine Stunde offen steht, sammelte sonst unbegrenzt. Fünfzig sind mehr, als jemand in einem Zug
 * zurückgeht, und wenig genug, um nicht ins Gewicht zu fallen.
 */
export const HISTORY_LIMIT = 50;

/**
 * Einen Schritt aufnehmen.
 *
 * Das Wiederherstellen verfällt dabei: Wer nach einem Rückgängig etwas Neues tut, hat sich für
 * einen anderen Weg entschieden, und der alte führt von hier aus nirgendwohin mehr.
 */
export const remember = (history: EditHistory, step: EditStep): EditHistory => ({
    past: [...history.past, step].slice(-HISTORY_LIMIT),
    future: [],
});

/** Den jüngsten Schritt herausnehmen — der Aufrufer führt ihn aus. */
export const undoStep = (history: EditHistory): { history: EditHistory; step: EditStep | null } => {
    if (history.past.length === 0) {
        return { history, step: null };
    }

    const step = history.past[history.past.length - 1];

    return {
        history: { past: history.past.slice(0, -1), future: [...history.future, step] },
        step,
    };
};

/** Und wieder zurück. */
export const redoStep = (history: EditHistory): { history: EditHistory; step: EditStep | null } => {
    if (history.future.length === 0) {
        return { history, step: null };
    }

    const step = history.future[history.future.length - 1];

    return {
        history: { past: [...history.past, step], future: history.future.slice(0, -1) },
        step,
    };
};

export const canUndo = (history: EditHistory): boolean => history.past.length > 0;

export const canRedo = (history: EditHistory): boolean => history.future.length > 0;

/** Was ein Rückgängig zurücknähme — oder `null`, wenn es nichts gibt. */
export const undoLabel = (history: EditHistory): string | null =>
    history.past.length > 0 ? history.past[history.past.length - 1].label : null;

/** Was ein Wiederherstellen zurückbrächte. */
export const redoLabel = (history: EditHistory): string | null =>
    history.future.length > 0 ? history.future[history.future.length - 1].label : null;
